// Firebase.tsx

// --- Core Firebase Imports ---
import { initializeApp, FirebaseApp } from "firebase/app";
import { getAnalytics, setAnalyticsCollectionEnabled, Analytics } from "firebase/analytics";
import { getStorage, FirebaseStorage } from 'firebase/storage';
import { getFirestore, Firestore } from 'firebase/firestore';
import {
  Auth,
  initializeAuth,
  browserLocalPersistence,
  browserSessionPersistence,
  inMemoryPersistence
} from "firebase/auth";

let app: FirebaseApp;
let analytics: Analytics;
let storage: FirebaseStorage;

/**
 * Named database: "app-{appId}"
 * Collections: orders, orders_test, orderBatches, orderTestBatches,
 *              restaurants, tokens, admin_data, mailingList,
 *              deliveryQuotes, system, otpCodes
 */
let firestore: Firestore;

/**
 * Default database (shared across all apps)
 * Collections: storeConfig, storeConfigCache, {appId}_users
 *
 * Note on document IDs in the default DB:
 *   storeConfig/{storeId}              — individual store configs (appId field stamped)
 *   storeConfig/{appId}_properties     — app-level config (privacy policy, terms, etc.)
 *   storeConfig/{appId}_status         — status sentinel doc
 *   storeConfigCache/{appId}_metadata  — cache metadata
 *   storeConfigCache/{appId}_data1...  — cache data parts
 *   storeConfigCache/{appId}_status    — cache status sentinel
 *   {appId}_users/{email}              — user permission documents
 */
let defaultFirestore: Firestore;

export let FIREBASE_AUTH: Auth;

const isIndexedDbAvailable = (timeout = 300): Promise<boolean> => {
  return new Promise((resolve) => {
    const timeoutId = setTimeout(() => {
      console.warn("IndexedDB availability check timed out.");
      resolve(false);
    }, timeout);
    try {
      const dbRequest = indexedDB.open("firebase-health-check");
      dbRequest.onsuccess = (event) => {
        clearTimeout(timeoutId);
        if (event.target && (event.target as IDBOpenDBRequest).result) {
          (event.target as IDBOpenDBRequest).result.close();
        }
        resolve(true);
      };
      dbRequest.onerror = () => {
        clearTimeout(timeoutId);
        resolve(false);
      };
    } catch (error) {
      clearTimeout(timeoutId);
      resolve(false);
    }
  });
};

const isSessionStorageAvailable = (): boolean => {
  try {
    const testKey = 'firebase-session-storage-test';
    sessionStorage.setItem(testKey, 'test');
    sessionStorage.removeItem(testKey);
    return true;
  } catch (e) {
    return false;
  }
};

export const initializeFirebase = async (firebaseConfig: {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
}, appId: string): Promise<void> => {
  if (FIREBASE_AUTH) return;

  app = initializeApp(firebaseConfig);
  analytics = getAnalytics(app);
  setAnalyticsCollectionEnabled(analytics, false);
  storage = getStorage(app);

  // Named DB — app-specific collections (orders, restaurants, tokens, etc.)
  console.log("ATTEMPTING TO CONNECT TO named DB: ", "app-" + appId);
  firestore = getFirestore(app, "app-" + appId);

  // Default DB — shared collections (storeConfig, storeConfigCache, {appId}_users)
  console.log("ATTEMPTING TO CONNECT TO default DB");
  defaultFirestore = getFirestore(app);
  defaultDbProbe = probeDefaultDb(firebaseConfig.projectId, firebaseConfig.apiKey);

  let persistence;
  if (await isIndexedDbAvailable()) {
    persistence = browserLocalPersistence;
    console.log("Firebase Auth: Using IndexedDB for local persistence.");
  } else if (isSessionStorageAvailable()) {
    persistence = browserSessionPersistence;
    console.warn("Firebase Auth: Falling back to session persistence.");
  } else {
    persistence = inMemoryPersistence;
    console.error("Firebase Auth: Falling back to in-memory persistence.");
  }

  FIREBASE_AUTH = initializeAuth(app, { persistence });
};

export { storage, analytics };
export { firestore, defaultFirestore };
export const FIREBASE_APP = () => app;

/** Named DB — use for orders, restaurants, tokens, and all other app-specific collections */
export const FIREBASE_FIRESTORE = (): Firestore => firestore;

/** Default DB — use for storeConfig, storeConfigCache, and {appId}_users */
export const FIREBASE_DEFAULT_FIRESTORE = (): Firestore => defaultFirestore;

// ── Does this project HAVE a (default) Firestore database? ─────────────────
// Some pools (dp-usa-1) only have the per-brand named databases. The web SDK
// never errors on a database that doesn't exist — it retries forever — so a
// default-first read there hung the whole site on its spinner (every US brand,
// found 2026-09-24). One REST call answers it definitively (404 "database
// (default) does not exist"); the answer is kept for a day per project.
let defaultDbProbe: Promise<boolean> = Promise.resolve(true);
async function probeDefaultDb(projectId: string, apiKey: string): Promise<boolean> {
  const key = `dp-defaultdb:${projectId}`;
  try {
    const hit = JSON.parse(localStorage.getItem(key) || "null");
    if (hit && Date.now() - hit.at < 24 * 3600_000) return hit.ok === true;
  } catch { /* fine */ }
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 4000);
    const res = await fetch(
      `https://firestore.googleapis.com/v1/projects/${encodeURIComponent(projectId)}/databases/(default)/documents/system/_probe?key=${encodeURIComponent(apiKey)}`,
      { signal: ctrl.signal }
    );
    clearTimeout(t);
    let ok = true;
    if (res.status === 404) {
      const body = await res.text();
      ok = !/database \(default\) does not exist/i.test(body);
    }
    try { localStorage.setItem(key, JSON.stringify({ ok, at: Date.now() })); } catch { /* fine */ }
    return ok;
  } catch {
    return true; // can't tell (offline / blocked): keep the old behaviour
  }
}
/** Resolves true when the project has a (default) database to read/write. */
export const DEFAULT_DB_AVAILABLE = (): Promise<boolean> => defaultDbProbe;

export const FIREBASE_AUTH_INSTANCE = (): Auth => {
  if (!FIREBASE_AUTH) throw new Error("Firebase Auth not initialized yet");
  return FIREBASE_AUTH;
};
export default { get current() { return app; } };